/**
 * The persistent game state (what goes into a save file) and pure helpers for
 * mutating it. No rendering or DOM access here.
 */
import type { SkillId } from '../data/skills';
import { SKILLS, SKILL_ORDER } from '../data/skills';
import { ZONES } from '../data/zones';
import {
  BAG_STEP,
  BASE_BAG,
  bagSizeFor,
  charmLimitFor,
  MAX_BAG_UPGRADES,
  MAX_LEVEL,
  xpToNext,
} from './balance';
import { activeCharms, equipTargetFor } from './items';
import { newAscendancy, type AscendancyState } from './ascendancy';
import { newParagon, paragonXpToNext, type ParagonState } from './paragon';
import type { ConsumableId, Difficulty, EquipSlot, Item, MaterialId } from './types';

export const SAVE_VERSION = 8;

export interface QuestState {
  id: string;
  stage: number;
  progress: number;
  done: boolean;
  /** Tracked in the HUD. */
  tracked?: boolean;
}

export interface Bounty {
  id: string;
  enemy: string;
  count: number;
  progress: number;
  zone: string;
  gold: number;
  xp: number;
  dust: number;
}

export interface HeroState {
  name: string;
  level: number;
  xp: number;
  hp: number;
  mp: number;
  gold: number;
  skillPoints: number;
  /** Skill ranks (0/undefined = locked). */
  skills: Partial<Record<SkillId, number>>;
  slots: (SkillId | null)[];
  passives: Record<string, number>;
  surge: number;
  surgeUnlocked: boolean;
  flaskHp: number;
  flaskMp: number;
  flaskUpgrades: number;
  /** Bag Expansions bought from Mira (+8 slots each). */
  bagUpgrades: number;
  /** Charm Satchels bought from Mira (+2 active charms each). */
  charmUpgrades: number;
  /** Levels past the cap (Diablo 3 style). */
  paragon: ParagonState;
}

export interface SaveData {
  version: number;
  slot: number;
  createdAt: number;
  savedAt: number;
  playTime: number;
  difficulty: Difficulty;
  ngPlus: number;
  hero: HeroState;
  inventory: Item[];
  equipment: Record<EquipSlot, Item | null>;
  consumables: Partial<Record<ConsumableId, number>>;
  materials: Partial<Record<MaterialId, number>>;
  /** Gem pouch: stack counts keyed by `${type}_${quality}` (see game/gems). */
  gems: Record<string, number>;
  flags: Record<string, number>;
  quests: Record<string, QuestState>;
  bounties: Bounty[];
  location: { map: string; x: number; y: number };
  respawn: { map: string; x: number; y: number };
  discovered: string[];
  explored: Record<string, string>;
  bestiary: Record<string, number>;
  stats: {
    kills: number;
    deaths: number;
    bosses: number;
    goldEarned: number;
    itemsFound: number;
    legendaries: number;
    abyssals: number;
    worldBosses: number;
    abyssBest: number;
    damageDealt: number;
  };
  droppedGold: { map: string; x: number; y: number; amount: number } | null;
  shop: { stock: Item[]; refreshedAt: number };
  /** Torment tier (0 = off) stacked on Nightmare after the story is beaten. */
  torment: number;
  /** Where an open town portal leads back to (null = none open). */
  townPortal: { map: string; x: number; y: number } | null;
  /** Ascendancy class, trials and notables (Path of Exile 2 style). */
  ascendancy: AscendancyState;
  /**
   * Legacy (v5): a per-slot stash. The stash is now shared between save slots
   * (see game/stash); anything left here is moved into it when the save loads.
   */
  stash?: { tabs: Item[][] };
  /** The world boss event in progress, if any. */
  worldBoss: WorldBossEvent | null;
  /** Play time when the next world boss event begins (-1 = not scheduled yet). */
  worldBossNext: number;
  /** The layout each zone currently uses (a new one is picked when you walk in from town). */
  layouts: Record<string, number>;
}

export interface WorldBossEvent {
  /** Enemy id of the world boss. */
  boss: string;
  zone: string;
  level: number;
  /** Where it stands in the zone (pixels). */
  x: number;
  y: number;
  /** Play time when it leaves if nobody is fighting it. */
  endsAt: number;
  /** Health left, so leaving the zone doesn't heal it. */
  hpFrac: number;
}

/** How many items the bag holds (grows with Bag Expansions). */
export const bagSize = (s: Pick<SaveData, 'hero'>): number => bagSizeFor(s.hero.bagUpgrades ?? 0);
/** How many charms in the bag are active at once. */
export const charmLimit = (s: Pick<SaveData, 'hero'>): number => charmLimitFor(s.hero.charmUpgrades ?? 0);
export const BASE_FLASK_HP = 4;
export const BASE_FLASK_MP = 3;

export function newGame(slot: number, name: string, difficulty: Difficulty): SaveData {
  const now = Date.now();
  return {
    version: SAVE_VERSION,
    slot,
    createdAt: now,
    savedAt: now,
    playTime: 0,
    difficulty,
    ngPlus: 0,
    hero: {
      name,
      level: 1,
      xp: 0,
      hp: 100,
      mp: 40,
      gold: 30,
      skillPoints: 0,
      skills: { slash: 1 },
      slots: ['slash', null, null, null],
      passives: {},
      surge: 0,
      surgeUnlocked: false,
      flaskHp: BASE_FLASK_HP,
      flaskMp: BASE_FLASK_MP,
      flaskUpgrades: 0,
      bagUpgrades: 0,
      charmUpgrades: 0,
      paragon: newParagon(),
    },
    inventory: [],
    equipment: {
      weapon: null,
      offhand: null,
      helm: null,
      shoulders: null,
      bracers: null,
      legs: null,
      amulet: null,
      armor: null,
      gloves: null,
      belt: null,
      ring1: null,
      ring2: null,
      boots: null,
    },
    consumables: { elixir: 1 },
    materials: {},
    gems: {},
    flags: {},
    quests: {},
    bounties: [],
    location: { map: 'town', x: -1, y: -1 },
    respawn: { map: 'town', x: -1, y: -1 },
    discovered: ['town_crystal'],
    explored: {},
    layouts: {},
    bestiary: {},
    stats: {
      kills: 0,
      deaths: 0,
      bosses: 0,
      goldEarned: 0,
      itemsFound: 0,
      legendaries: 0,
      abyssals: 0,
      worldBosses: 0,
      abyssBest: 0,
      damageDealt: 0,
    },
    droppedGold: null,
    shop: { stock: [], refreshedAt: 0 },
    torment: 0,
    townPortal: null,
    ascendancy: newAscendancy(),
    worldBoss: null,
    worldBossNext: -1,
  };
}

export const getFlag = (s: SaveData, f: string): number => s.flags[f] ?? 0;
export const hasFlag = (s: SaveData, f: string): boolean => (s.flags[f] ?? 0) > 0;
/** Has this zone guardian been fully defeated? (Malachar needs both of his forms.) */
export function bossCleared(s: SaveData, boss: string): boolean {
  if (boss === 'malachar') return hasFlag(s, 'boss_malachar_true');
  return hasFlag(s, `boss_${boss}`);
}

export function setFlag(s: SaveData, f: string, v = 1): void {
  s.flags[f] = v;
}

export interface LevelUpResult {
  levels: number;
  newSkills: SkillId[];
  /** Paragon levels gained (XP past the level cap). */
  paragonLevels: number;
}

/** Grant XP; handles multiple level-ups, skill points and skill unlocks. */
export function grantXp(s: SaveData, amount: number): LevelUpResult {
  const h = s.hero;
  const result: LevelUpResult = { levels: 0, newSkills: [], paragonLevels: 0 };
  if (h.level >= MAX_LEVEL) {
    const p = h.paragon;
    p.xp += Math.max(0, Math.round(amount));
    while (p.xp >= paragonXpToNext(p.level)) {
      p.xp -= paragonXpToNext(p.level);
      p.level++;
      result.paragonLevels++;
    }
    return result;
  }
  h.xp += Math.max(0, Math.round(amount));
  while (h.level < MAX_LEVEL && h.xp >= xpToNext(h.level)) {
    h.xp -= xpToNext(h.level);
    h.level++;
    h.skillPoints++;
    result.levels++;
    for (const id of SKILL_ORDER) {
      if (!SKILLS[id].challenge && SKILLS[id].unlockLevel === h.level && !h.skills[id]) {
        h.skills[id] = 1;
        result.newSkills.push(id);
        const free = h.slots.indexOf(null);
        if (free >= 0) h.slots[free] = id;
      }
    }
  }
  if (h.level >= MAX_LEVEL) h.xp = 0;
  return result;
}

/** Skills unlocked by level that the hero is missing (e.g. after loading an old save). */
export function syncUnlockedSkills(s: SaveData): void {
  for (const id of SKILL_ORDER) {
    const def = SKILLS[id];
    if (!def.challenge && def.unlockLevel <= s.hero.level && !s.hero.skills[id]) s.hero.skills[id] = 1;
  }
}

export function addGold(s: SaveData, amount: number): void {
  s.hero.gold = Math.max(0, s.hero.gold + Math.round(amount));
  if (amount > 0) s.stats.goldEarned += Math.round(amount);
}

export function addMaterial(s: SaveData, id: MaterialId, n = 1): void {
  s.materials[id] = Math.max(0, (s.materials[id] ?? 0) + n);
}

export function addConsumable(s: SaveData, id: ConsumableId, n = 1): void {
  s.consumables[id] = Math.max(0, (s.consumables[id] ?? 0) + n);
}

export function inventoryFull(s: SaveData): boolean {
  return s.inventory.length >= bagSize(s);
}

/**
 * Where everything in the bag sits: one cell per bag slot, not counting the charms in
 * the charm slots (they sit there instead). Items keep the cell they were put in
 * (`bagSlot`), so the bag can have gaps; anything new, or whose cell is gone or taken,
 * goes to the first empty cell. Returns the cells.
 */
export function layoutBag(s: SaveData): (Item | null)[] {
  const active = new Set(activeCharms(s));
  const items = s.inventory.filter((i) => !active.has(i));
  const cells: (Item | null)[] = new Array(Math.max(bagSize(s) - active.size, items.length)).fill(null);
  const unplaced: Item[] = [];
  for (const it of items) {
    const c = it.bagSlot;
    if (c !== undefined && Number.isInteger(c) && c >= 0 && c < cells.length && !cells[c]) cells[c] = it;
    else unplaced.push(it);
  }
  let free = 0;
  for (const it of unplaced) {
    while (cells[free]) free++;
    cells[free] = it;
    it.bagSlot = free;
  }
  for (const it of active) delete it.bagSlot;
  return cells;
}

/** Put a bag item in bag cell `cell`; whatever was there takes its old cell. */
export function moveToCell(s: SaveData, item: Item, cell: number): boolean {
  const cells = layoutBag(s);
  if (cell < 0 || cell >= cells.length || item.bagSlot === undefined) return false;
  const other = cells[cell];
  if (other === item) return false;
  if (other) other.bagSlot = item.bagSlot;
  item.bagSlot = cell;
  return true;
}

/** Pack the bag in list order (after sorting it), with no gaps. */
export function compactBag(s: SaveData): void {
  for (const it of s.inventory) delete it.bagSlot;
  layoutBag(s);
}

/** Add gear to the bag (in the first empty cell). Returns false if the bag is full. */
export function addItem(s: SaveData, item: Item): boolean {
  if (inventoryFull(s)) return false;
  delete item.bagSlot;
  s.inventory.push(item);
  layoutBag(s);
  s.stats.itemsFound++;
  if (item.rarity === 'legendary' || item.rarity === 'set') s.stats.legendaries++;
  if (item.rarity === 'abyssal') s.stats.abyssals++;
  return true;
}

/** Equip an item from the bag; the previously equipped item goes back to the bag. */
export function equipItem(s: SaveData, uid: string, target?: EquipSlot): Item | null {
  const idx = s.inventory.findIndex((i) => i.uid === uid);
  if (idx < 0) return null;
  const item = s.inventory[idx];
  const slot = target ?? equipTargetFor(s, item);
  if (!slot) return null; // charms are never equipped
  const prev = s.equipment[slot];
  s.inventory.splice(idx, 1);
  item.isNew = false;
  s.equipment[slot] = item;
  // the item it replaces takes its place in the bag
  if (prev) {
    s.inventory.splice(idx, 0, prev);
    if (item.bagSlot === undefined) delete prev.bagSlot;
    else prev.bagSlot = item.bagSlot;
  }
  delete item.bagSlot;
  layoutBag(s);
  return prev;
}

/** Take gear off into the first empty bag cell. */
export function unequip(s: SaveData, slot: EquipSlot): boolean {
  const it = s.equipment[slot];
  if (!it || inventoryFull(s)) return false;
  s.equipment[slot] = null;
  delete it.bagSlot;
  s.inventory.unshift(it);
  layoutBag(s);
  return true;
}

/**
 * Bag items that can be sold or salvaged: not locked, and not in the charm slots (a
 * charm has to go back to the bag first).
 */
export function sellableItems(s: SaveData): Item[] {
  const active = new Set(activeCharms(s));
  return s.inventory.filter((i) => !i.locked && !active.has(i));
}

export function removeItem(s: SaveData, uid: string): Item | null {
  const idx = s.inventory.findIndex((i) => i.uid === uid);
  if (idx < 0) return null;
  return s.inventory.splice(idx, 1)[0];
}

/**
 * Manual sorting: move the item at `from` onto slot `to` of a packed item list.
 * Onto another item, the two swap; onto an empty slot past the end, it moves to the end.
 */
export function moveInList(list: Item[], from: number, to: number): boolean {
  if (from < 0 || from >= list.length || to < 0 || from === to) return false;
  if (to >= list.length) {
    const [it] = list.splice(from, 1);
    list.push(it);
    return true;
  }
  [list[from], list[to]] = [list[to], list[from]];
  return true;
}

/**
 * Put a charm into the active charm slots. With a free slot it just becomes active;
 * when every slot is taken it trades places with the active charm in `slot` (default:
 * the last one), which then waits in the bag. Returns false if it isn't a bag charm.
 */
export function activateCharm(s: SaveData, uid: string, slot?: number): boolean {
  const i = s.inventory.findIndex((x) => x.uid === uid);
  const charm = s.inventory[i];
  if (!charm || charm.slot !== 'charm') return false;
  charm.charmOff = false;
  // with a free slot it is active now; otherwise trade places with an active charm
  const active = activeCharms(s);
  const at = active.indexOf(charm);
  const k = slot !== undefined && slot < active.length ? slot : at >= 0 ? at : active.length - 1;
  if (k === at || k < 0) {
    layoutBag(s);
    return true;
  }
  const j = s.inventory.indexOf(active[k]);
  [s.inventory[i], s.inventory[j]] = [s.inventory[j], s.inventory[i]];
  // the charm it replaces waits in the bag cell it came from
  if (charm.bagSlot !== undefined) active[k].bagSlot = charm.bagSlot;
  layoutBag(s);
  return true;
}

/** Take a charm out of the active charm slots (it stays in the bag, set aside). */
export function deactivateCharm(s: SaveData, uid: string): boolean {
  const charm = s.inventory.find((x) => x.uid === uid);
  if (!charm || charm.slot !== 'charm') return false;
  charm.charmOff = true;
  layoutBag(s);
  return true;
}

/** Find an item anywhere (bag or equipped). */
export function findItem(s: SaveData, uid: string): Item | null {
  return (
    s.inventory.find((i) => i.uid === uid) ?? Object.values(s.equipment).find((i) => i?.uid === uid) ?? null
  );
}

// -------------------------------------------------------------- explored ----

/** Encode a 0/1 byte array as a compact base64 bitset. */
export function encodeBits(bits: Uint8Array): string {
  const bytes = new Uint8Array(Math.ceil(bits.length / 8));
  for (let i = 0; i < bits.length; i++) if (bits[i]) bytes[i >> 3] |= 1 << (i & 7);
  let s = '';
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function decodeBits(str: string | undefined, length: number): Uint8Array {
  const out = new Uint8Array(length);
  if (!str) return out;
  try {
    const bin = atob(str);
    for (let i = 0; i < length; i++) {
      const b = bin.charCodeAt(i >> 3);
      if (b & (1 << (i & 7))) out[i] = 1;
    }
  } catch {
    /* corrupt data: treat as unexplored */
  }
  return out;
}

/** Bring older saves up to the current version. */
export function migrate(raw: unknown): SaveData | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Partial<SaveData>;
  if (typeof s.version !== 'number' || !s.hero || !s.equipment) return null;
  // Fill any fields added after the save was written.
  const base = newGame(s.slot ?? 0, s.hero.name ?? 'Kai', s.difficulty ?? 'normal');
  const merged: SaveData = {
    ...base,
    ...s,
    hero: { ...base.hero, ...s.hero, paragon: { ...base.hero.paragon, ...(s.hero.paragon ?? {}) } },
    stats: { ...base.stats, ...(s.stats ?? {}) },
    shop: s.shop ?? base.shop,
    version: SAVE_VERSION,
  } as SaveData;
  // v1 -> v2: a single `ring` slot became two ring fingers, plus gloves & belt
  const eq = { ...(s.equipment as unknown as Record<string, Item | null>) };
  if ('ring' in eq) {
    eq.ring1 = eq.ring1 ?? eq.ring;
    delete eq.ring;
  }
  merged.equipment = { ...base.equipment, ...eq } as Record<EquipSlot, Item | null>;
  // v2 -> v3: the gem pouch (added by the base merge above when missing)
  merged.gems = { ...(s.gems ?? {}) };
  // v3 -> v4: paragon (merged into hero above), Torment, town portal and ascendancy
  merged.ascendancy = { ...base.ascendancy, ...(s.ascendancy ?? {}) };
  merged.torment = s.torment ?? 0;
  // v4 -> v5: bag and charm limits became upgrades. Older saves keep what they had
  // (a 60-slot bag, rounded up to whole rows, and 10 active charms).
  if ((s.version ?? 0) < 5) {
    const need = Math.max(60, merged.inventory.length) - BASE_BAG;
    merged.hero.bagUpgrades = Math.min(MAX_BAG_UPGRADES, Math.max(0, Math.ceil(need / BAG_STEP)));
    merged.hero.charmUpgrades = 2;
  }
  // v5 -> v6: the stash is shared between slots; a per-slot stash is kept only until
  // the game moves it into the shared one (Game.setSave)
  if (!s.stash?.tabs?.some((t) => t.length)) delete merged.stash;
  // v7 -> v8: zones became bigger with several layouts; old map exploration no longer fits
  if ((s.version ?? 0) < 8) {
    merged.layouts = {};
    for (const id of Object.keys(merged.explored)) if (ZONES[id]) delete merged.explored[id];
  }
  syncUnlockedSkills(merged);
  return merged;
}
